import {BaseViewStore} from 'core/stores';
import applicationContext from 'core/utils/applicationContext';
import {ScheduleService, OrderingScheduleService} from 'application/schedule/services';
import moment from 'moment';
import _ from 'lodash';
import { ModalParams } from 'core/models';
import { action, observable, runInAction } from 'mobx';
import { toDate } from 'core/utils';
import { TasksCardStore, TaskNotificationsStore } from 'application/dashboard/stores';
import { TaskCommentService } from 'application/schedule/services';
import { PersonService } from 'application/person/services';
import { FacilityOrderService } from 'application/facility-order/services';
import { PickTicketHeaderService } from 'application/pick-ticket/services';

@applicationContext
class DashboardViewStore extends BaseViewStore {
    @observable accessor files;
    @observable accessor deliveryTasks;
    @observable accessor orderingTasks;
    @observable accessor futureTasks;
    @observable accessor userRoles;
    @observable accessor showNoRoleMessage = false;

    _interval = 30 * 1000; // 30 sec

    constructor(rootStore) {
        super(rootStore);

        this.scheduleService = new ScheduleService(rootStore.application.baasic.apiClient);
        this.orderingScheduleService = new OrderingScheduleService(rootStore.application.baasic.apiClient);
        this.taskCommentService = new TaskCommentService(rootStore.application.baasic.apiClient);
        this.personService = new PersonService(rootStore.application.baasic.apiClient);

        this.userRoles = this.rootStore.userStore.applicationUser.roles;

        this.goToPreview = (id) => {
            this.rootStore.routerStore.goTo(
                'master.app.main.import.detail.list',
                { id: id }
            );
        };
        this.orderingTaskModal = new ModalParams({ onSuccess: this.onOrderSuccess });
        this.assigneeCardStore = new TasksCardStore(rootStore,{
            fetchFunc: async () => {
                const commonParameters = {
                    calendarTypeId: this.orderType && this.orderType.id,
                    isOnHold: false,
                    sort:"date|desc",
                    embed: 'Facility,Facility.AccountRep,Facility.IntakeRep,Facility.PayorRep,Facility.DocumentRep,Facility.BillingRep,Facility.Corporate_,AreaOfCare,AccountRep,PayorRep,DocumentRep,BillingRep,IntakeRep,Facility.FacilityAreaOfCares,FacilityScheduleDates,FacilityOrder'
                }
                let roleFilterTrue = await this.setRoleFilter(true);
                if (!roleFilterTrue) {
                    // No linked person and/or no task-related role, so there is
                    // nothing to filter tasks by. Flag it so the template shows a
                    // message card instead of an empty task list.
                    runInAction(() => { this.showNoRoleMessage = true; });
                    return { data: { item: [] } };
                }
                runInAction(() => { this.showNoRoleMessage = false; });

                let reminderTasks = await this.scheduleService.find({
                    ...commonParameters,
                    ...roleFilterTrue,
                });
                let roleFilterFalse = await this.setRoleFilter(false);
                let assigneeTasks = await this.scheduleService.find({
                    ...commonParameters,
                    assigneeWithUserId: rootStore.userStore.user.id,
                    dateTo:this.setDate(),
                    isDone:false,
                    ...roleFilterFalse
                });
                assigneeTasks = assigneeTasks.data.item;
                reminderTasks = reminderTasks.data.item;

                return {
                    data: {
                        item: assigneeTasks.concat(reminderTasks).filter(Boolean)
                    }
                };
            },
            onChange: this.updateTask,
            reminder: this.reminder
        });

        this.accountManagerCardStore = new TasksCardStore(rootStore,{
            fetchFunc: async () => {
                return await this.scheduleService.getCurrentTasksByRoles({isOnHold:false,daysAhead:1,sort:"date|desc",embed: 'Facility,Facility.AccountRep,Facility.IntakeRep,Facility.PayorRep,Facility.DocumentRep,Facility.BillingRep,AreaOfCare,AccountRep,PayorRep,DocumentRep,BillingRep,IntakeRep,Facility.FacilityAreaOfCares,Facility.Corporate_,FacilityScheduleDates,FacilityOrder'});
            },
            onChange: this.updateTask
        });

        this.taskNotificationsStore = new TaskNotificationsStore(rootStore,{
            fetchFunc: async () => {
                return await this.taskCommentService.find({
                    isRead: false,
                    receiverUserId: rootStore.userStore.user.id,
                    embed: 'Author,FacilityScheduleDate,TaskCommentNotifications',
                    rpp:300
                });
            },
        });

        this.fetch([
            this.initialize()
        ]);
    }

    @action.bound
    async initialize() {
        try {
            const typeResponse = await this.scheduleService.getTypes();
            this.orderType = _.find(typeResponse.item, {abrv: 'ordering'});
            this.tasksFetch()

            if (this.taskListInterval) {
                clearInterval(this.taskListInterval);
            }
            this.taskListInterval = setInterval(
                this.tasksFetch,
                this._interval
            );
        } catch (err) {
            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to refresh tasks",
                err
            );
        }
    }

    destroy() {
        if (this.taskListInterval) {
            clearInterval(this.taskListInterval);
        }
        super.destroy();
    }

    @action.bound
    openFacilityEdit(id) {
        window.open(this.rootStore.routerStore.findRoute('master.app.main.facility.edit').pattern.replace(":id",id));
    }

    @action.bound
    openPickTicketEdit(facilityOrder, pickTicketHeaderId) {
        localStorage.setItem('refreshPickTicket', true);
        let route = facilityOrder.isNewFlow2 ? 'master.app.main.facility-order.pick-ticket-new-flow.list' : 'master.app.main.facility-order.pick-ticket.list';

        window.open(`${this.rootStore.routerStore
            .findRoute(route).pattern
            .replace(":facilityOrderId", facilityOrder.id)
            .replace(":pickTicketHeaderId", pickTicketHeaderId)}`);
    }

    @action.bound
    async openNikoFacilityOrder(facilityOrderId) {
        const facilityOrderService = new FacilityOrderService(this.rootStore.application.baasic.apiClient);
        const targetPage = (await facilityOrderService.getNikoTargetPage(facilityOrderId)).data;
        return window.open(`${this.rootStore.routerStore.findRoute('master.app.main.niko-facility-order.' + targetPage + '-preview').pattern.replace(':id', facilityOrderId)}`, '_blank');
    }

    @action.bound
    async downloadNikoPickTicketExcel(facilityOrderId) {
        const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
        const result = await pickTicketHeaderService.hasPickTicketItemsNiko(facilityOrderId, 'Pick Ticket');
        if (!result.hasItems) {
            this.rootStore.notificationStore.error("There are no items to include on the Pick Ticket.");
            return;
        }
        const url = `${pickTicketHeaderService.pickTicketReportDownloadUrlNiko(facilityOrderId)}?format=XLSX&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
        window.open(url);
    }

    async setRoleFilter(isReminder) {
        let appPerson = await this.personService.getByUser(this.rootStore.userStore.user.id);
        if (!appPerson || !appPerson.data) {
            // getByUser returns null when the logged-in user has no linked person record.
            return null;
        }

        if(this.userRoles.includes("IntakeSpecialist"))
        {
            return {
                isReminderIntake: isReminder,
                intakeRepId: appPerson.data.id
            }
        }
        else if(this.userRoles.includes("PayorSpecialist"))
        {
            return {
                isReminderPayor: isReminder,
                payorRepId: appPerson.data.id
            }
        }
        else if(this.userRoles.includes("DocumentSpecialist"))
        {
            return {
                isReminderDocument: isReminder,
                documentRepId: appPerson.data.id
            }
        }
        else if(this.userRoles.includes("Biller"))
        {
            return {
                isReminderBiller: isReminder,
                billerRepId: appPerson.data.id
            }
        }
    }

    @action.bound
    async tasksFetch() {
        if(this.userRoles.includes("Purchasers")){
            this.getUserFutureTasks();
        }else if(this.userRoles.includes("AccountManagers")){
            this.accountManagerCardStore.callFunc();
            this.taskNotificationsStore.callFunc();
        }else{
            this.assigneeCardStore.callFunc();
            this.taskNotificationsStore.callFunc();
        }
    }

    setDate(){
        let today = new Date();
        let userRoles = this.rootStore.userStore.user.roles;
        let days = 0;
        if(userRoles.includes("IntakeSpecialist") || userRoles.includes("PayorSpecialist")){
            days = 1;
        }
        return moment(today).add(days, 'days').format('YYYY-MM-DD');
    }

    @action.bound
    onOrderSuccess() {
        this.orderingTaskModal.close();
        this.tasksFetch();
    }

    @action.bound
    async onOrderTaskDetailChange(task) {
        this.orderingTaskModal.open({
            item: task,
            onSuccess: this.onOrderSuccess,
            clickCallback: () => this.orderingTaskModal.close()
        });
    }
    @action.bound
    async onFutureTaskDetailChange(task) {
        task.isDone = !task.isDone;
        this.orderingTaskModal.open({
            item: task,
            onSuccess: this.onOrderSuccess,
            clickCallback: (task) => this.clearCheck(task)
        });
    }

    @action.bound
    clearCheck(task) {
        this.orderingTaskModal.close();
        task.isDone = false;
    }

    @action.bound
    async updateTask(id, checked, task) {
        task[id] = checked;
        await this.scheduleService.update(task);
        await this.tasksFetch();
    }

    @action.bound
    async getUserFutureTasks() {
        const service = this.scheduleService;
        let paramsTomorrow = {
            fromDay: 1,
            rpp: 1000,
            embed: 'Facility,Facility.AccountRep,AreaOfCare,AccountRep,FacilityOrder',
            isOnHold: false
        };
        const tommorowTasks = await service.getFutureTasks(paramsTomorrow);
        const tasks = _.map(tommorowTasks.data.item, (item) => {
            return _.extend(item, {
                name: item.facility.name + (item.areaOfCare ? ', ' + item.areaOfCare.name : ''),
                isWoundCare: item.areaOfCare && item.areaOfCare.name === 'Wound Care',
                accRepName: item.accountRep ? item.accountRep.firstName + ' ' + item.accountRep.lastName : '',
                dateString: toDate(item.date),
                originalAccountRep: item.facility.accountRep && item.facility.accountRep.id != item.accountRep.id ?
                    item.facility.accountRep.firstName + ' ' + item.facility.accountRep.lastName : null,
                originalIntakeRep: item.facility.intakeRep && item.facility.intakeRep.id != item.intakeRep.id ?
                    item.facility.intakeRep.firstName + ' ' + item.facility.intakeRep.lastName : null,
                originalPayorRep: item.facility.payorRep && item.facility.payorRep.id != item.payorRep.id ?
                    item.facility.payorRep.firstName + ' ' + item.facility.payorRep.lastName : null,
                originalDocumentRep: item.facility.documentRep && item.facility.documentRep.id != item.documentRep.id ?
                    item.facility.documentRep.firstName + ' ' + item.facility.documentRep.lastName : null,
                originalBillingRep: item.facility.billingRep && item.facility.billingRep.id != item.billingRep.id ?
                    item.facility.billingRep.firstName + ' ' + item.facility.billingRep.lastName : null
            });
        });
        let grouped = _.groupBy(tasks, 'dateString');
        let tommorowTasksGroup = {};
        _.each(grouped, (val, key) => {
            tommorowTasksGroup[key]  = _.sortBy(val, i => i.name);
        });
        runInAction(() => {
            this.futureTasks = {
                data: tommorowTasksGroup,
                onChange: this.onFutureTaskDetailChange
            }
        });
    }
}

export default DashboardViewStore;
